import HOOK from "./hooks"
import { COLOR, COLUMN, DATES, FORMATTING, JARGON, TENSE_ENUM } from "../modules/constants"
import { CHECKBOX } from "../modules/checkbox_constants"
import { LEGEND } from "../modules/legends"
import { dobToDate, formatDate, getAge, getCheckboxName, getMonYear, getYearsAndMonths, nundefined } from "../modules/utilities"
import { COUNTRY } from "../modules/Countries"

const { veryLightGrey, lightGrey, greyText, darkGreyText, legendGreen } = COLOR.PDF
const { asc, desc, left, right } = FORMATTING
const hMargin = 40

const lineWidth = {
  thin: 0.5,
  medium: 1.25,
}

const defaultLayout = {
  hLineWidth: () => 0,
  vLineWidth: () => 0,
  paddingTop: () => 0, // padding above top row
  paddingBottom: () => 0, // padding under each row },
  paddingLeft: () => 0,
  paddingRight: () => 0,
}

const footerLayout = {
  ...defaultLayout,
  hLineWidth: i => i === 0 ? lineWidth.thin : 0,
  paddingTop: () => 15,
  hLineColor: lightGrey,
}

const infoLayout = {
  ...defaultLayout,
  hLineWidth: i => i === 0 ? lineWidth.thin : 0,
  paddingTop: i => i === 0 ? 15 : 0,
}

//#region  export
export const fontSize = {
  small: 8,
  medium: 10,
  large: 14,
  huge: 18,
}

// Table widths
export const width = {
  age: 40,
  committee: 45,
  date: 75,
  director: 150,
  gender: 50,
  index: 70, // FTSE SmallCap
  overlap: 50,
  remuneration: 80,
  role: 110,
  tenure: 70,
}

export const indent = 120

export const initTableLayouts = (brandColor, layout) => {

  defaultLayout.hLineColor = brandColor
  footerLayout.hLineColor = brandColor

  layout.info = {
    ...defaultLayout,
    hLineWidth: i => i === 0 ? lineWidth.thin : 0,
    paddingTop: i => i === 0 ? 15 : 0,
  }

  layout.title = {
    ...defaultLayout,
    hLineWidth: i => i === 0 ? lineWidth.thin : 0,
    paddingTop: i => i === 0 ? 20 : 0, // padding above top row
    paddingBottom: () => 10, // padding under each row },
  }

  layout.rolesLabel = {
    ...defaultLayout,
    hLineWidth: i => i === 0 ? lineWidth.thin : 0,
    paddingTop: () => 20, // padding above top row
    paddingBottom: () => 0, // padding under each row },
  }

  layout.core = {
    ...defaultLayout,
    hLineWidth: i => i === 0 ? lineWidth.thin : 0,
    paddingTop: () => 5,
    paddingBottom: i => i === 3 ? 0 : 3,
    paddingLeft: () => 3,
    paddingRight: () => 3,
  }

  layout.body = {
    ...defaultLayout,
    hLineColor: (i, node) => i === 0 || i === node.table.body.length ? brandColor : lightGrey,
    // hLineWidth: (i, node) => i === node.table.body.length ? 0 : i === 1 ? lineWidth.medium : lineWidth.thin,
    hLineWidth: (i, node) => i === 0 || i === node.table.body.length ? 0 : lineWidth.thin,
    paddingTop: i => i === 0 ? 7 : 5,
    paddingBottom: i => i === 0 ? 2 : 5,
    paddingRight: () => 5,
  }

  layout.details = {
    ...defaultLayout,
    hLineWidth: i => i === 0 ? lineWidth.thin : 0,
    paddingTop: i => i === 0 ? 15 : 3,
    paddingBottom: () => 3,
  }

  layout.committees = {
    ...defaultLayout,
    hLineColor: () => lightGrey,
    // hLineColor: i => i === 0 ? brandColor : lightGrey,
    // hLineWidth: (i, node) => i < 2 || i === node.table.body.length ? lineWidth.medium : lineWidth.thin,
    hLineWidth: (i, node) => i === 0 || i === node.table.body.length ? 0 : lineWidth.thin,
    paddingTop: i => i === 0 ? 5 : 5,
    paddingBottom: i => i === 0 ? 2 : 5,
    paddingRight: () => 5,
    vLineWidth: (i, node) => [3, 5, 7].includes(i) ? lineWidth.thin : 0,
    vLineColor: lightGrey,
  }

  layout.info = {
    ...defaultLayout,
    hLineWidth: i => i === 0 ? lineWidth.thin : 0,
    paddingTop: i => i === 0 ? 15 : 0,
  }

  layout.footer = footerLayout
}

export const docDefinition = {
  // a string or { width: number, height: number }
  pageSize: "A4",
  // by default we use portrait, you can change it to landscape if you wish
  pageOrientation: "landscape",
  // [left, top, right, bottom] or [horizontal, vertical] or just a number for equal margins
  pageMargins: [hMargin, 60, hMargin, 60],
}

export const getHeader = props => {
  const { cell = {}, alignment, prefix = "", sortId = 0, style } = props

  cell[sortId] = {
    alignment,
    sortId,
    style,
    text: prefix + LEGEND[sortId].label,
  }
  return cell[sortId]
}

export const styles = props => {
  // console.log("props: ", props)
  return {
    header: {
      fontSize: fontSize.medium,
      margin: [hMargin, 20, hMargin, 100]
    },
    titleTable: {
      fontSize: fontSize.medium,
      color: greyText,
    },
    coreTable: {
      fontSize: fontSize.medium,
      color: darkGreyText,
    },
    coreLabel: {
      fontSize: fontSize.large,
      paddingBottom: () => 0
    },
    coreLegend: {
      fontSize: fontSize.medium,
      color: legendGreen,
      fillColor: veryLightGrey,
      marginLeft: 4,
    },
    coreValue: {
      fontSize: fontSize.large,
      fillColor: veryLightGrey,
      marginLeft: 5,
    },
    bodyTable: {
      fontSize: fontSize.medium,
      color: darkGreyText,
      margin: [0, 10, 0, 15], // margins above and below whole table
    },
    tableHeader: {
      bold: true,
      fontSize: fontSize.medium,
    },
    detailsTable: {
      fontSize: fontSize.medium,
      color: darkGreyText,
    },
    infoTable: {
      fontSize: fontSize.medium,
      color: greyText,
    },
    footerTable: {
      fontSize: fontSize.medium,
      color: greyText,
      margin: [hMargin, 10, hMargin, 0]
    },
    link: {
      decoration: "underline",
      lineHeight: 1.6,
      alignment: left,
      color: props.brandColor,
      // bold: "true",
    },
    noCommittee: {
      fillColor: veryLightGrey,
    },
  }
}

export const header = ({ the350club }) => {
  return ({
    style: "header",
    layout: "noBorders",
    table: {
      widths: ["*", "*", "*"],
      body: the350club ?
        [
          [
            { image: "the350ClubLogo", link: "https://the350.club/", width: 40, alignment: left },
            {},
            { image: "numerableLogo", link: "https://www.numerable.io/?utm_source=350club&utm_medium=numapp&utm_campaign=cheatsheet", width: indent, alignment: right },
          ],
        ] :
        [
          [
            { image: "numerableLogo", link: "https://www.numerable.io/?utm_source=numerable&utm_medium=numapp&utm_campaign=cheatsheet", width: indent, bottomMargin: 100 },
            {},
            {}
          ],
        ],
    },
  })
}

export const footer = (currentPage, pageCount) => {
  return {
    style: "footerTable",
    layout: footerLayout,
    table: {
      widths: ["*", "*"],
      body:
        [
          [
            { text: "© Copyright Numerable Software " + new Date().getFullYear(), alignment: left },
            { text: "Page " + currentPage.toString() + " of " + pageCount, alignment: right }
          ],
        ]
    },
  }
}

export const infoTable = ({ the350club, tableLayouts }) => ({
  style: "infoTable",
  layout: tableLayouts.info,
  table: {
    headerRows: 1,
    widths: ["*"],
    body: [
      [{
        text: [
          { text: "Data from " },
          { text: "Numerable Software", link: "https://www.numerable.io/?utm_source=350club&utm_medium=numapp&utm_campaign=cheatsheet", style: "link" },
          { text: the350club ? " in partnership with " : "" },
          { text: the350club ? "the 350 Club" : "", link: "https://the350.club/", style: "link" },
        ]
      }],
      [{ text: JARGON.UPSELL_PHRASE }],
    ],
  },
})

const pad = n => (n < 10 ? '0' : '') + n

//#region sorting
export const sortArray = props => {
  // console.log('sortArray props:',props)
  const { array, headers, kind, orderSet, tableOrder } = props
  const { orderBy } = tableOrder[kind][orderSet]

  // Get the index of the header with sortId that matches the orderBy
  const index = headers.findIndex(x => x.sortId === orderBy)

  // If not already doing so, begin by sorting the 1st colum (name/company)
  if (index !== 0)
    sortRow(0, asc, array)

  if (index !== -1)
    sortRow(index, tableOrder[kind][orderSet].order, array)
  else // Report 'header not found' error
    for (var name in COLUMN)
      if (COLUMN[name] === orderBy)
        console.error(`orderBy: ${orderBy} - "${name}" - was not found`)
}

const sortRow = (index, order, array) => {
  array.sort((a, b) => {
    const values = getValue(a, b, index)

    return values.a === values.b
      ? 1
      : (values.a < values.b)
        ? order === desc ? 1 : -1
        : order === desc ? -1 : 1
  })
}

const getValue = (a, b, index) => typeof index === "object"
  // If index is an array (typeof "object") we use a[0][index[0]].
  ? {
    a: a[0][index[0]].sortValue,
    b: b[0][index[0]].sortValue
  }
  // Otherwise, b[index].
  : {
    a: a[index].sortValue,
    b: b[index].sortValue
  }
//#endregion sorting

export const get = {

  formattedDate: () => {

    var today = new Date()

    var dd = today.getDate()
    var mm = today.getMonth() + 1
    var yyyy = today.getFullYear()

    return `${pad(dd)}/${pad(mm)}/${yyyy}`
  },

  numberToLocalString: number => !nundefined(number)
    ? number.toLocaleString()
    : JARGON.UNKNOWN,

  numberToLocalStringGBP: number => !nundefined(number)
    ? number.toLocaleString("en-GB",
      {
        style: "currency",
        currency: "GBP",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })
    : JARGON.UNKNOWN,

  appointedToBoard: (from, alignment) => ({
    text: from ? formatDate(from) : "",
    alignment
  }),

  remunerationObj: (compensationGBP, alignment) => ({
    text: get.numberToLocalStringGBP(compensationGBP),
    alignment
  }),

  resignedFromBoard: (toDate, role, alignment) => ({
    text: role.tense === TENSE_ENUM.FUTURE
      ? "n/a"
      : role.until
        ? formatDate(role.until)
        : toDate // The end date of the company
          ? formatDate(toDate)
          : JARGON.UNKNOWN,
    alignment
  }),

  sex: sex => sex === 0 ? "male" : sex === 1 ? "female" : JARGON.UNKNOWN,

  yearsAndMonths: (from, until, alignment) => ({
    text: from ? getYearsAndMonths(from, until) : JARGON.UNKNOWN,
    alignment
  }),

  // index: getName(indexBit),
  index: indexBit => indexBit ? getCheckboxName(CHECKBOX[HOOK.LSE_INDEX], indexBit) : JARGON.OTHER.toLowerCase(),

  indexBit: props => props.via?.indexBit ?? props.entity.companyProps?.lse?.indexBit,
  // Handle legacy entity structure
  name: entity => entity?.commonProps.name ?? entity.name,

  getDateOfBirth: dob => getMonYear(dobToDate(dob)) || JARGON.UNKNOWN,

  // SortValue methods
  positionSortValue: positionTitle => positionTitle, // TODO
  indexSortValue: indexBit => indexBit ?? 99,
  toDateSortValue: toDate => toDate ? new Date(toDate).getTime() : DATES.FUTURE.getTime(),
  fromSortValue: from => from ? new Date(from).getTime() : 0,
  compensationSortValue: compensation => compensation > 0 ? compensation : 0,
  yearsTenureSortValue: days => days ? days : 0,
  ageSortValue: personInfo => personInfo && personInfo.age ? personInfo.age : 0,
  sexSortValue: personInfo => personInfo && personInfo.sex ? personInfo.sex : 9, // Sort ascending m > f > unknown
  committeeObj: (cmtes, alignment) => ({ text: cmtes ? '√' : "", alignment, })
}

export const titleTable = ({ hubData, tableLayouts }) =>
({
  style: "titleTable",
  layout: tableLayouts.title,
  table: {
    // headerRows: 1,
    widths: ["80%", "*"],
    body:
      [
        [
          { text: hubData.name, fontSize: fontSize.huge, bold: "true", color: darkGreyText },
          { text: JARGON.CHEAT_SHEET + " at " + get.formattedDate(), fontSize: fontSize.small, alignment: right },
        ],
      ]
  },
})

export const rolesLabel = props => {
  // console.log('props:', props)
  const { nodes, tableLayouts } = props
  return nodes.length === 1
    ? null
    : {
      style: "coreLabel",
      layout: tableLayouts.rolesLabel,
      table: {
        widths: ["*", "*"],
        body: [
          props.committees
            ? [{ text: "Committee Roles" }, {}]
            : [{ text: "Board Roles" }, {}],
        ]
      },
    }
}

export const detailsTable = ({ hubData, tableLayouts }) => {
  const { linkedIn } = hubData
  // console.log('hubData:', hubData)
  let bodyArray = [
    [{
      text: [
        { text: "Age: ", bold: "true" },
        { text: getAge(hubData.dateOfBirth), color: greyText }
      ]
    }],
    [{
      text: [
        { text: "Date of Birth: ", bold: "true" },
        { text: getDateOfBirth(hubData.dateOfBirth), color: greyText }
      ]
    }],
    [{
      text: [
        { text: "Gender: ", bold: "true" },
        { text: get.sex(hubData.sex), color: greyText }
      ]
    }],
    [{
      text: [
        { text: `${JARGON.NATIONALITY}: `, bold: "true" },
        { text: concatNationalities(hubData.nationalities), color: greyText }
      ]
    }],
    [{
      text: [
        { text: `${JARGON.RESIDENCE}: `, bold: "true" },
        { text: concatCountries(hubData.countries), color: greyText }
      ]
    }],
  ]

  if (linkedIn)
    bodyArray.push([{
      text: [
        { text: "LinkedIn: ", bold: "true" },
        { text: linkedIn, style: "link" }
      ]
    }])

  return ({
    style: "detailsTable",
    layout: tableLayouts.details,
    table: {
      headerRows: 1,
      widths: ["*"],
      body: bodyArray,
    },
  })
}

const concatNationalities = nationalities => nationalities.map(c => COUNTRY[c].nationality ?? JARGON.UNKNOWN).join(", ")
const concatCountries = countries => countries.map(c => COUNTRY[c].country ?? JARGON.UNKNOWN).join(", ")
const getDateOfBirth = dob => getMonYear(dobToDate(dob)) || JARGON.UNKNOWN
//#endregion export

